import { RequestTypes } from 'legacy/lut/classes/models/onboarding/Onboarding'
import React, { useCallback, useMemo, useState } from 'react'

import { ModalDialog } from 'core-components/library/modalDialog'
import { warning } from 'onboarding/classes/services/notification'

import { IViewAllCriteriaModalProps, ViewAllCriteriaModal } from '../viewAllCriteria'
import { ViewAllCriteriaButton } from '../viewAllCriteriaButton/ViewAllCriteriaButton'

import { useActiveSpecializationCriteriaHandlers } from './useActiveSpecializationCriteriaHandlers'

export const useActiveSpecializationCriteriaElements = () => {
  const [isCriteriaOpen, setIsCriteriaOpen] = useState(false)
  const {
    fetchSpecializationCriteria,
    fetchRevenueCriteria,
    resetSpecializationCriteria,
    resetRevenueCriteriaThresholds,
    specializationCriteria,
    revenueCriteriaThresholds,
    isSpecializationCriteriaLoading,
    isRevenueCriteriaLoading,
  } = useActiveSpecializationCriteriaHandlers()

  const onOpen = useCallback(
    async ({ code, requestType, countryISO }: IOpenParams) => {
      if (!(code && requestType && countryISO)) {
        warning('Required data is not provided to get criteria details')
      }

      setIsCriteriaOpen(true)
      await Promise.all([
        fetchSpecializationCriteria(code, requestType),
        fetchRevenueCriteria(code, countryISO),
      ])
    },
    [fetchSpecializationCriteria, fetchRevenueCriteria]
  )
  const onClose = useCallback(() => {
    setIsCriteriaOpen(false)
    resetSpecializationCriteria()
    resetRevenueCriteriaThresholds()
  }, [resetSpecializationCriteria, resetRevenueCriteriaThresholds])

  const composeViewAllCriteriaButton = useCallback(
    (openParams: IOpenParams, { isDisabled }: IButtonProps) => {
      return <ViewAllCriteriaButton onOpen={() => onOpen(openParams)} isDisabled={isDisabled} />
    },
    [onOpen]
  )

  const viewAllCriteriaModalToRender = useMemo(() => {
    return (
      <ModalDialog<IViewAllCriteriaModalProps>
        show={isCriteriaOpen}
        content={ViewAllCriteriaModal}
        contentProps={{
          thresholds: revenueCriteriaThresholds || undefined,
          specializationName: specializationCriteria?.Name || '',
          content: specializationCriteria?.Content || '',
          onClose,
          isLoading: isSpecializationCriteriaLoading || isRevenueCriteriaLoading,
        }}
        onClose={onClose}
      />
    )
  }, [
    isCriteriaOpen,
    onClose,
    specializationCriteria?.Name,
    specializationCriteria?.Content,
    revenueCriteriaThresholds,
    isSpecializationCriteriaLoading,
    isRevenueCriteriaLoading,
  ])

  return { composeViewAllCriteriaButton, viewAllCriteriaModalToRender }
}

interface IOpenParams {
  code: string
  requestType: RequestTypes
  countryISO: string
}

interface IButtonProps {
  isDisabled: boolean
}
